/**
 * Links dentro do texto de uma mensagem — puro, testável sem tela.
 *
 * Mora fora do componente pelo mesmo motivo de `etiquetas.js` e `horario.js`:
 * o que se decide aqui é o que vira `<a href>` na tela do atendente, com texto
 * que **veio de fora** — o paciente digita o que quiser. Um erro aqui não é
 * cosmético, é o caminho clássico de XSS.
 *
 * A defesa tem três camadas, e nenhuma sozinha basta:
 *
 * 1. O padrão só reconhece `http://`, `https://` e `www.`. `javascript:`,
 *    `data:` e `file:` nunca chegam a ser candidatos.
 * 2. Todo candidato passa por `new URL()` e tem o `protocol` conferido. É esta
 *    a garantia de verdade — a regex é filtro grosso, não autoridade.
 * 3. A renderização é por elementos React, **nunca** `dangerouslySetInnerHTML`.
 *    É o React escapando o texto que fecha a porta.
 *
 * Candidato que não passa não vira link: fica como texto corrido, visível e
 * inerte. Falhar para o lado de "não linkar" é sempre o lado barato.
 */

/**
 * Candidato a URL: `http(s)://` ou `www.`, até o primeiro espaço.
 *
 * `\b` na frente impede casar no meio de uma palavra — `foohttps://x` não é
 * link. Aspas e sinais de menor/maior ficam fora do conjunto para que um
 * endereço colado dentro de aspas não engula a aspa final.
 */
const PADRAO_URL = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;

/** Pontuação que quase sempre é da FRASE, não do endereço. */
const PONTUACAO_FINAL = '.,;:!?…';

const contar = (texto, caractere) => texto.split(caractere).length - 1;

/**
 * Tira a pontuação da frase que grudou no fim do endereço.
 *
 * "veja http://site.com." não pode virar um link para `site.com.` — o ponto é
 * do período. Parênteses e colchetes só saem quando estão **desbalanceados**:
 * há URL legítima que os contém (a Wikipédia é cheia delas), e cortar o fecho
 * de um par completo quebraria o endereço.
 */
function aparar(bruto) {
  let url = bruto;
  while (url.length > 1) {
    const ultimo = url.at(-1);

    if (PONTUACAO_FINAL.includes(ultimo)) {
      url = url.slice(0, -1);
      continue;
    }
    if (ultimo === ')' && contar(url, '(') < contar(url, ')')) {
      url = url.slice(0, -1);
      continue;
    }
    if (ultimo === ']' && contar(url, '[') < contar(url, ']')) {
      url = url.slice(0, -1);
      continue;
    }
    break;
  }
  return url;
}

/**
 * O `href` seguro de um candidato, ou `null` se ele não for um endereço web.
 *
 * Devolve a forma normalizada pelo `URL` — já com escapes resolvidos — e não o
 * texto cru. O que o atendente LÊ continua sendo o texto original; o que o
 * navegador SEGUE passou pelo parser.
 */
export function paraHref(bruto) {
  const texto = String(bruto ?? '').trim();
  if (!texto) return null;

  // `www.site.com` é endereço para quem escreve, mas não tem esquema. Assumir
  // https (e não http) é a escolha certa: se o site não tiver TLS o navegador
  // avisa, enquanto o caminho contrário rebaixaria uma conexão segura.
  const comEsquema = /^www\./i.test(texto) ? `https://${texto}` : texto;

  try {
    const url = new URL(comEsquema);
    // A trava que importa. Tudo que não for web vira texto inerte.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

/**
 * Quebra o texto em segmentos `{ tipo, valor, href }`.
 *
 * `tipo` é `'texto'` ou `'link'`; `href` só existe no link. Os segmentos de
 * texto preservam **tudo** — espaços e quebras de linha inclusive —, porque a
 * bolha depende de `white-space: pre-wrap` para manter os parágrafos que o
 * paciente escreveu.
 *
 * Concatenar os `valor` na ordem devolve o texto original, com uma exceção
 * deliberada: nada é perdido, mas a pontuação aparada volta ao segmento de
 * texto seguinte.
 */
export function segmentarTexto(texto) {
  const bruto = String(texto ?? '');
  if (!bruto) return [];

  const segmentos = [];
  let cursor = 0;

  for (const achado of bruto.matchAll(PADRAO_URL)) {
    const url = aparar(achado[0]);
    const href = paraHref(url);

    // Candidato reprovado (esquema estranho, host impossível) segue como texto:
    // o cursor não anda e ele entra no próximo pedaço corrido.
    if (!href) continue;

    if (achado.index > cursor) {
      segmentos.push({ tipo: 'texto', valor: bruto.slice(cursor, achado.index) });
    }
    segmentos.push({ tipo: 'link', valor: url, href });
    cursor = achado.index + url.length;
  }

  if (cursor < bruto.length) {
    segmentos.push({ tipo: 'texto', valor: bruto.slice(cursor) });
  }
  return segmentos;
}

/**
 * Hosts do YouTube, comparados por igualdade exata.
 *
 * Exata, e não "termina com youtube.com", porque `meu-youtube.com.br` e
 * `youtube.com.attacker.net` passariam por um teste frouxo — e a capa é o
 * cartão em que a pessoa clica confiando no que vê.
 */
const HOSTS_YOUTUBE = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
]);

/** O id do YouTube tem 11 caracteres, sempre. Serve de sanidade para a capa. */
const ID_VALIDO = /^[A-Za-z0-9_-]{11}$/;

/** Segmentos de caminho que carregam o id logo depois de si. */
const CAMINHOS_COM_ID = new Set(['shorts', 'embed', 'live', 'v']);

/**
 * O id do vídeo, ou `null` se a URL não for do YouTube.
 *
 * Cobre `watch?v=`, `youtu.be/`, `shorts/`, `embed/`, `live/` e os subdomínios
 * `www.`, `m.` e `music.`. Parâmetros extras (`&t=`, `&list=`, `?si=`) são
 * ignorados de graça, porque a leitura é pelo `URL`, não por regex — foi por
 * isso que escolhi o parser aqui.
 */
export function idDoYoutube(url) {
  const texto = String(url ?? '').trim();
  if (!texto) return null;

  let endereco;
  try {
    endereco = new URL(/^www\./i.test(texto) ? `https://${texto}` : texto);
  } catch {
    return null;
  }
  if (endereco.protocol !== 'http:' && endereco.protocol !== 'https:') return null;

  const host = endereco.hostname.toLowerCase();
  if (!HOSTS_YOUTUBE.has(host)) return null;

  const partes = endereco.pathname.split('/').filter(Boolean);
  let id = null;

  if (host === 'youtu.be' || host === 'www.youtu.be') id = partes[0];
  else if (partes[0] === 'watch') id = endereco.searchParams.get('v');
  else if (CAMINHOS_COM_ID.has(partes[0])) id = partes[1];

  // Formato conferido antes de devolver: o id vai virar `src` de uma imagem, e
  // um id torto renderia um cartão quebrado dentro da bolha.
  return id && ID_VALIDO.test(id) ? id : null;
}

/**
 * A capa pública do vídeo.
 *
 * `hqdefault` é o único tamanho que o YouTube garante para todo vídeo —
 * `maxresdefault` falta em vídeo antigo ou de baixa resolução, e o cartão
 * apareceria vazio sem aviso.
 */
export const capaDoYoutube = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

/**
 * O PRIMEIRO vídeo do YouTube citado no texto, ou `null`.
 *
 * Primeiro, e não todos: uma mensagem com cinco links viraria uma parede de
 * capas maior que a conversa. Os demais continuam clicáveis como link.
 */
export function primeiroYoutube(texto) {
  for (const segmento of segmentarTexto(texto)) {
    if (segmento.tipo !== 'link') continue;
    const id = idDoYoutube(segmento.href);
    if (id) return { id, url: segmento.href, capa: capaDoYoutube(id) };
  }
  return null;
}
